import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, SendHorizontal, RotateCcw, ChevronDown, Maximize2, SlidersHorizontal, AudioLines } from "lucide-react";
import { Message } from "@/components/chat/Message";
import { VoiceButton } from "@/components/chat/VoiceButton";
import { VoiceMode, openVoiceSession } from "@/components/chat/VoiceMode";
import { FundFinder } from "@/components/chat/FundFinder";
import { CeresMark } from "@/components/ceres/CeresMark";
import { speech, spokenText } from "@/lib/speech";
import { SpeakingBar } from "@/components/chat/SpeakingBar";
import { useChat } from "@/components/chat/useChat";
import { t } from "@/lib/i18n";
import { trackDocClick, useSessionTracking } from "@/lib/track";

const Audiences = ({ s, onChip }) => {
  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-hv-rule bg-white" data-testid="chat-scenarios">
      <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="chat-scenarios-toggle"
        className="flex w-full items-center justify-between px-3 py-2 font-heading text-[12px] font-semibold text-hv-navy">
        {s.moreIdeas}<ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="border-t border-hv-rule p-3">
          <div role="tablist" aria-label={s.moreIdeas} className="flex gap-0.5">
            {s.audiences.map(([name], i) => (
              <button key={name} role="tab" type="button" aria-selected={tab === i} onClick={() => setTab(i)} data-testid={`chat-audience-tab-${i}`}
                className={`whitespace-nowrap px-1.5 py-1 font-heading text-[10.5px] font-semibold ${tab === i ? "bg-hv-navy text-white" : "text-hv-text hover:bg-hv-section"}`}>{name}</button>
            ))}
          </div>
          <ul role="tabpanel" className="mt-2 space-y-1">
            {s.audiences[tab][1].map((q, i) => (
              <li key={q}><button type="button" onClick={() => onChip(q)} data-testid={`chat-scenario-${tab}-${i}`}
                className="w-full px-2 py-1.5 text-left text-[13px] text-hv-logo-navy transition-colors hover:bg-hv-section">{q}</button></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const Welcome = ({ s, ticker, brand, onChip, onFinder }) => (
  <div className="space-y-4" data-testid="chat-welcome">
    <CeresMark size={48} animate className="-ml-1" />
    <p className="text-[14px] text-hv-text">{ticker ? s.welcomeTicker(ticker) : brand === "hhis" ? s.welcomeHhis : s.welcome}</p>
    <p className="border-l-2 hv-accent-border bg-white px-3 py-2 text-[13px] text-hv-text" data-testid="chat-advice-notice">{brand === "hhis" ? s.noticeHhis : s.notice}</p>
    <div className="flex flex-wrap gap-2">
      {s.chips.map((c, i) => (
        <button key={c} type="button" onClick={() => onChip(c)} data-testid={`chat-prompt-chip-${i}`}
          className="border border-hv-navy bg-white px-3 py-1.5 font-heading text-[12px] font-semibold text-hv-navy transition-colors hover:bg-hv-navy hover:text-white">{c}</button>
      ))}
    </div>
    <button type="button" onClick={onFinder} data-testid="chat-fund-finder-button"
      className="flex w-full items-center gap-3 border border-hv-navy bg-hv-navy px-3 py-2.5 text-left text-white transition-colors hover:bg-hv-mark">
      <SlidersHorizontal size={16} aria-hidden="true" />
      <span>
        <span className="block font-heading text-[13px] font-semibold">{s.finder}</span>
        <span className="block text-[11px] text-white/75">{s.finderIntro}</span>
      </span>
    </button>
    <Audiences s={s} onChip={onChip} />
  </div>
);

export const ChatPanel = ({ page, theme, onClose, expandTo, command, full = false, initialLang = "en" }) => {
  const lang = initialLang;
  const s = t(lang);
  const { messages, busy, send, cancel, feedback, reset, push, sessionId } = useChat({ lang, page });
  useSessionTracking(sessionId);
  const [input, setInput] = useState("");
  const [finder, setFinder] = useState(false);
  const [voiceNote, setVoiceNote] = useState("");

  const [voice, setVoice] = useState(null);
  const startVoiceMode = async () => {
    try {
      setVoice(await openVoiceSession());
      setVoiceNote("");
    } catch {
      setVoiceNote(s.micError);
    }
  };
  const endVoice = useCallback(() => setVoice(null), []);
  const voiceOk = typeof window !== "undefined" && !!navigator.mediaDevices?.getUserMedia && !!window.MediaRecorder;
  const speakNext = useRef(false);
  const lastCmd = useRef(null);
  useEffect(() => {
    if (!command || lastCmd.current === command.id) return;
    lastCmd.current = command.id;
    if (command.action === "ask" && command.prompt) send(command.prompt);
    if (command.action === "finder") setFinder(true);
    if (command.action === "voice") startVoiceMode();
  }, [command]); // eslint-disable-line react-hooks/exhaustive-deps
  const onVoice = (text, err) => {
    setVoiceNote(err || "");
    if (text) {
      speakNext.current = true;
      send(text);
    }
  };
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!speakNext.current || !last || last.role !== "assistant" || last.pending) return;
    speakNext.current = false;
    if (last.text && !last.error) speech.speak(last.id, spokenText(last.text, lang));
  }, [messages, lang]);
  useEffect(() => () => speech.stop(), []);

  const onFinderResult = (data, labels) => {
    setFinder(false);
    const now = Date.now();
    push(
      { id: `u-${now}`, role: "user", text: `${s.finder}: ${labels.join(" · ")}` },
      { id: `a-${now}`, role: "assistant", text: `${data.total ? s.finderResult(data.total, labels) : s.finderNone}\n\n${data.note}`,
        components: data.total ? [data] : [], sources: [{ id: 1, url: "https://harvestportfolios.com/products/", title: "Product Suite - Harvest ETFs", date: data.items[0]?.as_at }], local: true },
    );
  };
  const inputRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [messages, finder]);

  const submit = (e) => {
    e.preventDefault();
    send(input);
    setInput("");
  };

  return (
    <div className="relative flex h-full flex-col bg-hv-section" data-testid="chat-panel" onClickCapture={trackDocClick(sessionId)} onAuxClickCapture={trackDocClick(sessionId)}>
      {voice && <VoiceMode s={s} lang={lang} session={voice} messages={messages} send={send} cancel={cancel} onExit={endVoice} />}
      <header className="flex items-center justify-between gap-2 border-b border-hv-rule bg-white px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <img src={theme.header_logo} alt={page.brand === "hhis" ? "Harvest High Income Shares" : "Harvest ETFs"} className="h-5 w-auto shrink-0" />
          <span className="h-5 w-px bg-hv-rule" aria-hidden="true" />
          <h2 className="truncate whitespace-nowrap font-heading text-[14px] font-bold text-hv-navy" id="chat-title">{s.title}</h2>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {expandTo && (
            <Link to={expandTo} aria-label={s.expand} title={s.expand} className="p-1 text-hv-muted hover:text-hv-navy" data-testid="chat-expand-link"><Maximize2 size={16} /></Link>
          )}
          {messages.length > 0 && (
            <button type="button" onClick={reset} aria-label={s.newChat} className="p-1 text-hv-muted hover:text-hv-navy" data-testid="chat-new-button"><RotateCcw size={16} /></button>
          )}
          {onClose && (
            <button type="button" onClick={onClose} aria-label={s.close} className="p-1 text-hv-muted hover:text-hv-navy" data-testid="chat-close-button"><X size={18} /></button>
          )}
        </div>
      </header>
      <div className="flex-1 overflow-y-auto px-4 py-5" role="log" aria-live="polite" aria-labelledby="chat-title" data-testid="chat-messages">
        <div className={`space-y-5 ${full ? "mx-auto max-w-3xl" : ""}`}>
          <Welcome s={s} ticker={page.ticker} brand={page.brand} onChip={send} onFinder={() => setFinder(true)} />
          {messages.map((m) => <Message key={m.id} msg={m} lang={lang} onFeedback={feedback} />)}
          {finder && <FundFinder s={s} onResult={onFinderResult} onClose={() => setFinder(false)} />}
          <div ref={endRef} />
        </div>
      </div>
      <form onSubmit={submit} className="border-t border-hv-rule bg-white p-3">
        <div className={`flex items-end gap-2 ${full ? "mx-auto max-w-3xl" : ""}`}>
          <label htmlFor="chat-input" className="sr-only">{s.placeholder}</label>
          <textarea id="chat-input" ref={inputRef} rows={1} value={input} maxLength={2000} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(e); }} placeholder={s.placeholder} data-testid="chat-input"
            className="max-h-32 min-h-[42px] flex-1 resize-none border border-hv-rule px-3 py-2.5 text-[14px] text-hv-text placeholder:text-hv-muted focus:border-hv-navy focus:outline-none" />
          {voiceOk && (
            <button type="button" onClick={startVoiceMode} disabled={busy} aria-label={s.voiceMode} title={s.voiceMode} data-testid="chat-voice-mode-button"
              className="flex h-[42px] w-[42px] shrink-0 items-center justify-center border-2 border-hv-navy bg-white text-hv-navy transition-colors hover:bg-hv-section disabled:opacity-40">
              <AudioLines size={18} />
            </button>
          )}
          <VoiceButton s={s} lang={lang} disabled={busy} onText={(t2, e) => { speech.stop(); onVoice(t2, e); }} />
          <button type="submit" disabled={busy || !input.trim()} aria-label={s.send} data-testid="chat-send-button"
            className="flex h-[42px] w-[42px] items-center justify-center bg-hv-navy text-white transition-opacity disabled:opacity-40"><SendHorizontal size={18} /></button>
        </div>
        <SpeakingBar s={s} full={full} />
        <p className={`mt-1.5 text-[11px] text-hv-muted ${full ? "mx-auto max-w-3xl" : ""}`} role="status" data-testid="chat-input-note">{voiceNote || s.privacy}</p>
      </form>
    </div>
  );
};
